/**
 * Two-Factor Authentication Setup Component
 * Handles 2FA enrollment, verification, and management
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import API_URL from '../../config/api';

const Container = styled.div``;

const Status = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props.$enabled ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)'};
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$enabled ? '#4CAF50' : '#9E9E9E'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
`;

const StatusText = styled.div``;

const StatusLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
  opacity: 0.7;
`;

const StatusValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Button = styled.button`
  background: ${props => props.$danger ? '#f44336' : 'var(--accent-primary)'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$danger ? '#d32f2f' : 'var(--accent-secondary)'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const SetupModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: var(--accent-primary);
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ModalSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionLabel = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const QRContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const Secret = styled.div`
  background: var(--bg-secondary);
  padding: 1rem;
  border-radius: 8px;
  font-family: monospace;
  font-size: 1.1rem;
  text-align: center;
  word-break: break-all;
  margin-bottom: 0.5rem;
`;

const HelpText = styled.div`
  font-size: 0.85rem;
  color: var(--text-primary);
  opacity: 0.7;
  line-height: 1.5;
`;

const HelpTextWithMargin = styled(HelpText)`
  margin-bottom: 1rem;
`;

const HelpTextCompact = styled(HelpText)`
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  text-align: center;
  font-family: monospace;
  letter-spacing: 0.25rem;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: var(--text-primary);
  border: 2px solid var(--border-color);

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--accent-primary);
  }
`;

const InstructionList = styled.ol`
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  line-height: 1.8;
`;

function TwoFactorSetup({ enabled, onStatusChange }) {
  const { token } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEnableStart = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate 2FA setup');
      }

      setQrCode(data.qrCode);
      setSecret(data.secret);
      setShowModal(true);
    } catch (error) {
      console.error('2FA setup error:', error);
      toast.error(error.message || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Verification code must be 6 digits');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: verificationCode,
          secret: secret
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid verification code');
      }

      toast.success('Two-Factor Authentication enabled successfully!');
      setShowModal(false);
      setVerificationCode('');
      setQrCode(null);
      setSecret('');
      onStatusChange(true);
    } catch (error) {
      console.error('2FA verification error:', error);
      toast.error(error.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable Two-Factor Authentication? This will make your account less secure.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to disable 2FA');
      }

      toast.success('Two-Factor Authentication disabled');
      onStatusChange(false);
    } catch (error) {
      console.error('2FA disable error:', error);
      toast.error(error.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setVerificationCode('');
    setQrCode(null);
    setSecret('');
  };

  return (
    <Container>
      <Status $enabled={enabled}>
        <StatusInfo>
          <StatusIcon $enabled={enabled}>
            {enabled ? '✓' : '○'}
          </StatusIcon>
          <StatusText>
            <StatusLabel>Two-Factor Authentication</StatusLabel>
            <StatusValue>{enabled ? 'Enabled' : 'Disabled'}</StatusValue>
          </StatusText>
        </StatusInfo>
        
        {enabled ? (
          <Button $danger onClick={handleDisable} disabled={loading}>
            {loading ? 'Processing...' : 'Disable 2FA'}
          </Button>
        ) : (
          <Button onClick={handleEnableStart} disabled={loading}>
            {loading ? 'Setting up...' : 'Enable 2FA'}
          </Button>
        )}
      </Status>

      {showModal && (
        <SetupModal onClick={(e) => e.target === e.currentTarget && handleCancel()}>
          <ModalContent>
            <ModalTitle>Enable Two-Factor Authentication</ModalTitle>

            <ModalSection>
              <SectionLabel>Step 1: Install an Authenticator App</SectionLabel>
              <HelpText>
                <InstructionList>
                  <li>Google Authenticator (iOS/Android)</li>
                  <li>Microsoft Authenticator (iOS/Android)</li>
                  <li>Authy (iOS/Android/Desktop)</li>
                </InstructionList>
              </HelpText>
            </ModalSection>

            <ModalSection>
              <SectionLabel>Step 2: Scan QR Code</SectionLabel>
              <HelpTextWithMargin>
                Open your authenticator app and scan this QR code:
              </HelpTextWithMargin>
              <QRContainer>
                <QRCodeSVG value={qrCode} size={200} />
              </QRContainer>
            </ModalSection>

            <ModalSection>
              <SectionLabel>Manual Entry (if you can't scan)</SectionLabel>
              <HelpTextCompact>
                Enter this secret key in your authenticator app:
              </HelpTextCompact>
              <Secret>{secret}</Secret>
              <HelpText>
                Keep this secret safe! You can use it to set up 2FA on another device.
              </HelpText>
            </ModalSection>

            <ModalSection>
              <SectionLabel>Step 3: Enter Verification Code</SectionLabel>
              <HelpTextWithMargin>
                Enter the 6-digit code from your authenticator app:
              </HelpTextWithMargin>
              <Input
                type="text"
                maxLength="6"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                onKeyPress={(e) => e.key === 'Enter' && handleVerifyAndEnable()}
              />
            </ModalSection>

            <ButtonGroup>
              <CancelButton onClick={handleCancel} disabled={loading}>
                Cancel
              </CancelButton>
              <Button
                onClick={handleVerifyAndEnable}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </SetupModal>
      )}
    </Container>
  );
}

export default TwoFactorSetup;
